import {
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  MoreHorizontal,
  PenTool,
  Send,
  Trash2
} from "lucide-react";
import { useState, type FC, type MouseEvent, type ReactNode } from "react";

import type {
  ChecklistEntry,
  ChecklistSlot
} from "@/api/types/foreignNationalDocument";
import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { documentFocusDomId } from "../../documentsModel";
import {
  byline,
  caseTags,
  entryTitle,
  expiryClass,
  expiryText,
  isPdfEntry
} from "./checklistModel";

/** Everything a row needs from the checklist: the day, the client, selection and the actions. */
export type ChecklistRowContext = {
  today: Date;
  clientName: string | null;
  selecting: boolean;
  isSelected: (entry: ChecklistEntry) => boolean;
  onToggleSelect: (entry: ChecklistEntry) => void;
  highlightedId: string | null;
  onOpen: (entry: ChecklistEntry) => void;
  onDownload: (entry: ChecklistEntry) => void;
  onSendToCase: (entry: ChecklistEntry) => void;
  /** Absent when the firm can't ask for signatures here. */
  onAskToSign?: (entry: ChecklistEntry) => void;
  onSetShared: (entry: ChecklistEntry, shared: boolean) => void;
  onDelete: (entry: ChecklistEntry) => void;
  onMarkReviewed: (entry: ChecklistEntry) => void;
  onAskForRescan: (entry: ChecklistEntry) => void;
};

const ROW =
  "grid grid-cols-[18px_220px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-2";

const stop = (e: MouseEvent) => e.stopPropagation();

const firstName = (ctx: ChecklistRowContext) => ctx.clientName || "Client";

/** The row's "…" menu. Sharing with the client lives here, not on the row. */
const MoreMenu: FC<{ entry: ChecklistEntry; ctx: ChecklistRowContext }> = ({
  entry,
  ctx
}) => {
  const name = firstName(ctx);
  const canSign =
    Boolean(ctx.onAskToSign) && !entry.client_upload && isPdfEntry(entry);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="More"
          onClick={stop}
          data-testid={`checklist-more-${entry.id}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56" onClick={stop}>
        <DropdownMenuItem onSelect={() => ctx.onOpen(entry)}>
          <ArrowUpRight />
          Open
        </DropdownMenuItem>
        {entry.download_url ? (
          <DropdownMenuItem onSelect={() => ctx.onDownload(entry)}>
            <Download />
            Download
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => ctx.onSendToCase(entry)}>
          <Send />
          Send to Case
        </DropdownMenuItem>
        {canSign ? (
          <DropdownMenuItem onSelect={() => ctx.onAskToSign?.(entry)}>
            <PenTool />
            Ask {name} to Sign
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        {entry.client_upload ? (
          <DropdownMenuItem disabled>
            <Eye />
            Visible to {name} · Client Upload
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={() => ctx.onSetShared(entry, !entry.shared_with_client)}
            data-testid={`checklist-share-${entry.id}`}
          >
            {entry.shared_with_client ? <EyeOff /> : <Eye />}
            {entry.shared_with_client
              ? `Stop Sharing With ${name}`
              : `Share With ${name}`}
          </DropdownMenuItem>
        )}
        {entry.document_id ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => ctx.onDelete(entry)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** File icon that becomes a checkbox on hover, and stays one while anything is selected. */
const Lead: FC<{ entry: ChecklistEntry; ctx: ChecklistRowContext }> = ({
  entry,
  ctx
}) => {
  const checked = ctx.isSelected(entry);
  return (
    <span className="flex size-4.5 items-center justify-center">
      <span
        className={cn(
          "flex",
          ctx.selecting || checked ? "hidden" : "group-hover/row:hidden"
        )}
      >
        <FileTypeIcon
          name={entry.filename ?? entry.title ?? undefined}
          mimeType={entry.mime_type ?? undefined}
          size={16}
        />
      </span>
      <span
        className={cn(
          "items-center justify-center",
          ctx.selecting || checked ? "flex" : "hidden group-hover/row:flex"
        )}
        onClick={(e) => {
          e.stopPropagation();
          ctx.onToggleSelect(entry);
        }}
      >
        <Checkbox
          checked={checked}
          aria-label={`Select ${entry.filename ?? entry.title ?? "document"}`}
          data-testid={`checklist-select-${entry.id}`}
        />
      </span>
    </span>
  );
};

type EntryRowProps = {
  entry: ChecklistEntry;
  slot?: ChecklistSlot;
  ctx: ChecklistRowContext;
  /** `older` rows sit under the current one: no checkbox, no state button. */
  variant?: "current" | "older" | "other";
  olderToggle?: ReactNode;
};

/** One document: a slot's current copy, an older version, or an untyped upload in Other. */
export const ChecklistEntryRow: FC<EntryRowProps> = ({
  entry,
  slot,
  ctx,
  variant = "current",
  olderToggle
}) => {
  const older = variant === "older";
  const other = variant === "other";
  const checked = !older && ctx.isSelected(entry);
  const selectable = !older;
  const highlighted =
    entry.document_id !== null && ctx.highlightedId === entry.document_id;
  const title = other
    ? entry.filename || entry.title || "Document"
    : entryTitle(entry, slot);
  const unread = !older && entry.review === "new";
  const flagged = !older && entry.review === "flagged";

  const onRowClick = () => {
    if (ctx.selecting && selectable) ctx.onToggleSelect(entry);
    else ctx.onOpen(entry);
  };

  return (
    <div
      id={entry.document_id ? documentFocusDomId(entry.document_id) : undefined}
      className={cn(
        ROW,
        "group/row cursor-pointer",
        checked ? "bg-fill-2" : "hover:bg-fill-1",
        highlighted && "bg-accent-soft"
      )}
      title={entry.filename ?? undefined}
      onClick={onRowClick}
      data-testid={`checklist-entry-${entry.id}`}
      data-variant={variant}
    >
      {selectable ? (
        <Lead entry={entry} ctx={ctx} />
      ) : (
        <FileTypeIcon
          name={entry.filename ?? entry.title ?? undefined}
          mimeType={entry.mime_type ?? undefined}
          size={16}
        />
      )}
      <div className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              "truncate text-base font-medium",
              older ? "text-fg-2" : "text-fg"
            )}
          >
            {title}
          </span>
          {unread ? <StatusBadge tone="accent">New</StatusBadge> : null}
          {flagged ? <StatusBadge tone="danger">Flagged</StatusBadge> : null}
        </span>
        {other ? (
          <span className="text-sm text-fg-4">Type not set</span>
        ) : older && entry.state === "superseded" ? (
          <span className="text-sm text-fg-3">Superseded</span>
        ) : (
          <span className={cn("truncate text-sm", expiryClass(entry))}>
            {expiryText(entry, ctx.today)}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-col">
        {entry.summary ? (
          <span className="truncate text-sm text-fg-2">{entry.summary}</span>
        ) : null}
        <span className="flex min-w-0 items-center gap-1 text-sm text-fg-3">
          <span className="truncate">
            {byline(entry, ctx.today, ctx.clientName)}
          </span>
          {olderToggle}
        </span>
      </div>
      <div className="flex items-center justify-end gap-1.5">
        {ctx.selecting && !older ? null : (
          <>
            {unread ? (
              <Button
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.onMarkReviewed(entry);
                }}
                data-testid={`checklist-mark-reviewed-${entry.id}`}
              >
                <Check />
                Mark Reviewed
              </Button>
            ) : flagged ? (
              <Button
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.onAskForRescan(entry);
                }}
              >
                <Send />
                Ask for Rescan
              </Button>
            ) : (
              <>
                {entry.signature && !older ? (
                  <StatusBadge tone="warn">Awaiting Signature</StatusBadge>
                ) : null}
                {caseTags(entry).map((label) => (
                  <Tag key={label}>{label}</Tag>
                ))}
              </>
            )}
            <MoreMenu entry={entry} ctx={ctx} />
          </>
        )}
      </div>
    </div>
  );
};

/** One slot: its current documents, with older versions folded under the first. */
export const ChecklistSlotRow: FC<{
  slot: ChecklistSlot;
  ctx: ChecklistRowContext;
}> = ({ slot, ctx }) => {
  const [open, setOpen] = useState(false);
  if (slot.entries.length === 0) return null;
  const showOlder = open && !ctx.selecting;
  const toggle =
    slot.older.length > 0 ? (
      <button
        type="button"
        className="flex shrink-0 items-center gap-0.5 text-fg-2 hover:text-fg"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        data-testid={`checklist-older-${slot.key}`}
      >
        <span className="text-fg-4">·</span>
        {slot.older.length} older
        {open ? (
          <ChevronDown className="size-3" />
        ) : (
          <ChevronRight className="size-3" />
        )}
      </button>
    ) : null;
  return (
    <>
      {slot.entries.map((entry, i) => (
        <ChecklistEntryRow
          key={entry.id}
          entry={entry}
          slot={slot}
          ctx={ctx}
          olderToggle={i === 0 ? toggle : null}
        />
      ))}
      {showOlder
        ? slot.older.map((entry) => (
            <ChecklistEntryRow
              key={entry.id}
              entry={entry}
              slot={slot}
              ctx={ctx}
              variant="older"
            />
          ))
        : null}
    </>
  );
};
